import React, { useState, useRef, useEffect } from 'react'

/* Line icons (Heroicons-style) replacing the old emoji glyphs so the menu matches
 * the rest of the UI. All inherit currentColor at 1rem. */
const I: React.FC<{ d: string; d2?: string; fill?: boolean }> = ({ d, d2, fill }) => (
  <svg className="w-4 h-4" fill={fill ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    {d2 && <path strokeLinecap="round" strokeLinejoin="round" d={d2} />}
  </svg>
)
const EyeIcon = () => <I d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" d2="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
const TransferIcon = () => <I d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
const EnvelopeIcon = () => <I d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
const DocumentIcon = () => <I d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
const PrinterIcon = () => <I d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5z" />
const LinkIcon = () => <I d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
const BoltIcon = () => <I d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
const BlockIcon = () => <I d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
const ArchiveIcon = () => <I d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />

interface ChatControlsMenuProps {
  chatId: string
  chatStatus: string
  sourceIp?: string
  onTransfer: () => void
  onBlock: () => void
  onDelete: () => void
  onPrint: () => void
  onSavePdf: () => void
  onEmailTranscript: () => void
  onToggleEvents: () => void
  showEvents: boolean
  onToggleInfo?: () => void
  showInfo?: boolean
}

export const ChatControlsMenu: React.FC<ChatControlsMenuProps> = ({
  chatId: _chatId,
  chatStatus,
  sourceIp,
  onTransfer,
  onBlock,
  onDelete,
  onPrint,
  onSavePdf,
  onEmailTranscript,
  onToggleEvents,
  showEvents,
  onToggleInfo,
  showInfo,
}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const items = [
    { label: showInfo ? 'Hide Conversation Info' : 'View Conversation Info', icon: <EyeIcon />, onClick: onToggleInfo || (() => {}), show: !!onToggleInfo },
    { label: 'Transfer Chat', icon: <TransferIcon />, onClick: onTransfer, show: chatStatus === 'open' },
    { label: 'Email Transcript', icon: <EnvelopeIcon />, onClick: onEmailTranscript, show: true },
    { label: 'Save as PDF', icon: <DocumentIcon />, onClick: onSavePdf, show: true },
    { label: 'Print Chat', icon: <PrinterIcon />, onClick: onPrint, show: true },
    { label: 'Copy Chat URL', icon: <LinkIcon />, onClick: () => { navigator.clipboard.writeText(window.location.href); setOpen(false) }, show: true },
    { label: showEvents ? 'Hide System Events' : 'Show System Events', icon: <BoltIcon />, onClick: onToggleEvents, show: true },
    { label: 'Block Visitor IP', icon: <BlockIcon />, onClick: onBlock, show: !!sourceIp && chatStatus === 'open' },
    { label: 'Archive Chat', icon: <ArchiveIcon />, onClick: onDelete, show: true, danger: true },
  ].filter(i => i.show)

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="p-1.5 rounded-lg text-txt-secondary hover:text-txt-primary hover:bg-surface-hover border border-border transition-colors"
        title="Chat controls"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 rounded-lg border border-border bg-surface-elevated shadow-float z-50 py-1 animate-slide-up">
          {items.map((item, i) => (
            <button
              key={i}
              onClick={() => { item.onClick(); setOpen(false) }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 transition-colors ${
                (item as any).danger
                  ? 'text-error hover:bg-red-50 dark:hover:bg-red-900/20'
                  : 'text-txt-primary hover:bg-surface-hover'
              }`}
            >
              <span className={`flex items-center justify-center w-5 h-5 shrink-0 ${(item as any).danger ? '' : 'text-txt-secondary'}`}>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
